import { useState, useEffect, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import {
  updateTrip,
  createTrip,
  deleteTrip as deleteTripAction,
  resetTrip as resetTripAction,
} from "../../../../../../store/admin/tripsStore";
import { useTripsList } from "../../../../../../features/trips/queries";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import { useActiveTripsStore } from "../../../../../../store/admin/activeTripsStore";
import { useAlert } from "../../../../../../context/AlertContext";
import { useTranslation } from "react-i18next";
import type { Trip, TripFormData, ViewType } from "../types";

import { DIALOG_COLORS } from "../../../../../../components/ui";

export function useTripForm() {
  const { t } = useTranslation("adminSections");

  // Modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);

  // Filter states
  const [selectedStatus, setSelectedStatus] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedView, setSelectedView] = useState<ViewType>("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const debouncedSearch = useDebounce(searchQuery);

  // Form states
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  // Alert
  const { showAlert } = useAlert();

  const filterParams = useMemo(
    () => ({
      page: currentPage,
      limit: itemsPerPage,
      status_id:
        selectedStatus !== "all" && selectedStatus !== ""
          ? selectedStatus
          : undefined,
      search: debouncedSearch || undefined,
      device_id: selectedDeviceId || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }),
    [currentPage, itemsPerPage, selectedStatus, debouncedSearch, selectedDeviceId, dateFrom, dateTo],
  );

  const tripsQuery = useTripsList(filterParams);
  const trips = tripsQuery.data?.data ?? [];
  const meta = tripsQuery.data?.meta;
  const loading = tripsQuery.isLoading;
  const error = tripsQuery.error ? "Error" : null;

  const queryClient = useQueryClient();
  const invalidateTrips = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["trips"] });
  }, [queryClient]);

  // Map sidebar reads from useActiveTripsStore (cached up to 60s). After any
  // trip mutation we force-refetch so the map's "active trip" badge reflects
  // the change immediately instead of waiting for the next 60s interval.
  const refreshActiveTrips = useCallback(() => {
    useActiveTripsStore.getState().fetchActiveTrips(true);
  }, []);

  // React Hook Form
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    trigger,
    formState: { errors },
  } = useForm<TripFormData>();

  // Watch form values for React Select
  const watchedValues = watch();

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStatus, searchQuery, selectedDeviceId, dateFrom, dateTo]);

  // Handlers
  const handleEdit = (trip: Trip) => {
    setSelectedTrip(trip);
    // Only set new model fields
    setValue("title", trip.title || "");
    setValue("description", trip.description);
    setValue("route_id", trip.route_id || "");
    setValue("status_id", trip.status_id || "");
    setValue("priority", trip.priority || "");
    setValue("assignments", trip.assignments?.map(a => ({
      device_id: a.device_id,
      date: a.date,
      start_time: a.start_time,
      end_time: a.end_time,
      status: a.status,
      timezone: a.timezone,
    })) || []);
    setValue("notifiedUsers", trip.notifiedUsers || []);
    setValue("sendEmails", trip.sendEmails ?? false);
    setValue("comment", trip.comment);
    setValue("shipment_ids", undefined);
    setShowEditModal(true);
  };

  const handleDetails = (trip: Trip) => {
    setSelectedTrip(trip);
    setShowDetailsModal(true);
  };

  const handleOpenCreateModal = () => {
    reset();
    setShowCreateModal(true);
  };

  const handleCloseModals = () => {
    setShowDetailsModal(false);
    setShowEditModal(false);
    setShowCreateModal(false);
    setSelectedTrip(null);
  };

  const onSubmitEdit = async (data: TripFormData) => {
    if (!selectedTrip) return;

    setLoadingSubmit(true);

    // Build update data with only the new model fields
    const allowedFields = ["title", "description", "route_id", "status_id", "priority", "notifiedUsers", "sendEmails", "comment", "shipment_ids"];
    const updateData: Record<string, unknown> = {};
    
    Object.entries(data).forEach(([key, value]) => {
      if (allowedFields.includes(key) && value !== undefined && value !== null && value !== "") {
        updateData[key] = value;
      }
    });

    await updateTrip(
      selectedTrip.id,
      updateData,
      showAlert,
      setLoadingSubmit,
      t,
      () => {
        setShowEditModal(false);
        invalidateTrips();
        refreshActiveTrips();
      },
      reset
    );
  };

  const onSubmitCreate = async (data: TripFormData) => {
    setLoadingSubmit(true);

    // Build create data with only the new model fields
    const allowedFields = ["title", "description", "route_id", "status_id", "priority", "assignments", "notifiedUsers", "sendEmails", "comment", "shipment_ids"];
    const createData: Record<string, unknown> = {};
    
    Object.entries(data).forEach(([key, value]) => {
      if (allowedFields.includes(key) && value !== undefined && value !== null && value !== "") {
        createData[key] = value;
      }
    });

    await createTrip(
      createData,
      showAlert,
      setLoadingSubmit,
      t,
      () => {
        setShowCreateModal(false);
        invalidateTrips();
        refreshActiveTrips();
      },
      reset
    );
  };

  const handleDelete = (trip: Trip) => {
    showAlert({
      title: t("trips.delete.confirmTitle"),
      text: t("trips.delete.confirmText"),
      icon: "warning",
      confirmButtonText: t("trips.delete.confirm"),
      confirmButtonColor: DIALOG_COLORS.danger,
      showCancelButton: true,
      cancelButtonText: t("trips.actions.cancel"),
      onConfirm: () => {
        deleteTripAction(trip.id, showAlert, t, () => {
          invalidateTrips();
          refreshActiveTrips();
        });
      },
    });
  };

  const handleResetTrip = async (trip: Trip) => {
    showAlert({
      title: t("trips.reset.confirmTitle"),
      text: t("trips.reset.confirmText"),
      icon: "warning",
      confirmButtonText: t("trips.actions.reset"),
      confirmButtonColor: DIALOG_COLORS.warning,
      showCancelButton: true,
      cancelButtonText: t("trips.actions.cancel"),
      onConfirm: async () => {
        const result = await resetTripAction(trip.id);
        if (result.success) {
          showAlert({
            title: t("trips.reset.successTitle"),
            text: t("trips.reset.successText"),
            icon: "success",
            confirmButtonText: "OK",
            onConfirm: () => {
              invalidateTrips();
              refreshActiveTrips();
            },
          });
        } else {
          showAlert({
            title: t("Error"),
            text: result.error || t("trips.reset.errorText"),
            icon: "error",
            confirmButtonText: "OK",
            onConfirm: () => {},
          });
        }
      },
    });
  };

  // Wrapper functions
  const handleEditSubmit = async (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();
    const isFormValid = await trigger();
    if (!isFormValid) return;
    await handleSubmit(onSubmitEdit)(e);
  };

  const handleCreateSubmit = async (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();
    const isFormValid = await trigger();
    if (!isFormValid) return;
    await handleSubmit(onSubmitCreate)(e);
  };

  const handleStatusFilterChange = (status: string) => {
    setSelectedStatus(status);
    setCurrentPage(1);
  };

  const handleSearchChange = (search: string) => {
    setSearchQuery(search);
    setCurrentPage(1);
  };

  const handleDeviceFilterChange = (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    setCurrentPage(1);
  };

  const handleDateFromChange = (date: string) => {
    setDateFrom(date);
    setCurrentPage(1);
  };

  const handleDateToChange = (date: string) => {
    setDateTo(date);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSelectedStatus("");
    setSearchQuery("");
    setSelectedDeviceId("");
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
  };

  return {
    // Data
    trips,
    meta,
    loading,
    error,
    selectedTrip,
    loadingSubmit,

    // Form
    register,
    errors,
    setValue,
    reset,
    watchedValues,

    // Modal states
    showDetailsModal,
    showEditModal,
    showCreateModal,

    // Filter states
    selectedStatus,
    searchQuery,
    selectedDeviceId,
    dateFrom,
    dateTo,
    selectedView,
    currentPage,
    itemsPerPage,

    // Handlers
    handleEdit,
    handleDetails,
    handleDelete,
    handleResetTrip,
    handleOpenCreateModal,
    handleCloseModals,
    handleEditSubmit,
    handleCreateSubmit,
    handleStatusFilterChange,
    handleSearchChange,
    handleDeviceFilterChange,
    handleDateFromChange,
    handleDateToChange,
    handleClearFilters,
    setSelectedView,
    setCurrentPage,
  };
}
